// apps/hyperseguros-web/src/lib/i18n/LocaleProvider.tsx

'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createI18n, type Ti18nLocale, type Ti18nParams } from 'i18n'

type TT = (key: string, params?: Ti18nParams) => string

type TLocaleContext = {
  locale: Ti18nLocale
  t: TT
}

const LocaleContext = createContext<TLocaleContext | null>(null)

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Ti18nLocale
  children: ReactNode
}) {
  const value = useMemo<TLocaleContext>(() => {
    const i18n = createI18n(undefined, {
      defaultLocale: locale,
      missingKeyBehavior: 'placeholder',
    })
    i18n.locale(locale)
    return {
      locale,
      t: (key, params) => i18n.t(key, params),
    }
  }, [locale])

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  )
}

export function useT(): TT {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useT must be used inside <LocaleProvider>')
  }
  return ctx.t
}

export function useLocale(): Ti18nLocale {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useLocale must be used inside <LocaleProvider>')
  }
  return ctx.locale
}
